/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import classNames from 'classnames';
import { useRef, type ComponentPropsWithoutRef, type FC, type ReactNode, type SVGProps } from 'react';

import { getButtonClasses, getButtonLayout, getIconClasses } from './Button.helpers';

export interface Props extends ComponentPropsWithoutRef<'button'> {
  /**
   * ClassName to be applied to the button
   */
  className?: string;
  /**
   * Children to be rendered inside the button
   */
  children?: ReactNode;
  /**
   * Whether the button is active
   */
  active?: boolean;
  /**
   * Variant to set the display style
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'plain' | 'link';
  /**
   * Size of the button
   */
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  /**
   * Should fit width
   */
  stretch?: boolean;
  /**
   * Icon component to display
   */
  icon?: FC<SVGProps<SVGElement>>;
  /**
   * Icon positionning (Before or After the text)
   */
  iconPosition?: 'before' | 'after';
  /**
   * ClassName to be applied to the icon
   */
  iconClassName?: string;
}

export const Button = ({
  className = '',
  active,
  variant = 'primary',
  children,
  size = 'large',
  stretch,
  icon: Icon,
  iconPosition = 'after',
  iconClassName,
  ...rest
}: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonHoverRef = useRef<HTMLSpanElement>(null);
  const layout = getButtonLayout(Icon, children, iconPosition);
  const buttonClasses = classNames(`button-${variant} button-${size} button-${layout}`, className, getButtonClasses(layout, variant, size));
  const iconClasses = classNames(iconClassName, getIconClasses(children, size, iconPosition));

  const buttonMouseMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    if ((variant === 'primary' || variant === 'secondary') && !rest.disabled) {
      const btnHover = buttonHoverRef.current;
      const btn = e.target as HTMLElement;
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      btnHover?.style.setProperty('--x', x.toString() + 'px');
      btnHover?.style.setProperty('--y', y.toString() + 'px');
    }
  };

  const buttonHoverClasses = classNames(
    `bg-follow-${variant}`,
    {
      'bg-follow': !rest.disabled,
      'bg-follow-icon': layout === 'icon',
    }
  );

  return (
    <button
      className={buttonClasses}
      type={rest.type || 'button'}
      ref={buttonRef}
      data-active={active}
      onMouseMove={(e) => {
        buttonMouseMove(e);
      }}
      {...rest}
    >
      <span className={classNames(
        'relative z-1',
        'flex items-center',
        'size-full',
        'pointer-events-none',
        {
          'justify-between self-stretch': stretch,
          'justify-center': !stretch,
        }
      )}>
        {Icon && iconPosition === 'before' && <Icon className={iconClasses} />}
        {children}
        {Icon && iconPosition === 'after' && <Icon className={iconClasses} />}
      </span>
      <span ref={buttonHoverRef} className={buttonHoverClasses} />
    </button>
  );
};
